import type {
  ApiLogEntry,
  AuthResult,
  BindResult,
  CapabilitiesResult,
  DashboardSummary,
  Device,
  DeviceListResult,
  HealthResult,
  LocationHistoryResult,
  AlertPositionState,
  AlertStatus,
  LocalAlert,
  LocalDeviceMetadata,
  LocalDeviceMetadataUpdate,
  LoginRequest,
  RefreshManyResult,
  RefreshResult,
  RegisterRequest,
  SessionInfo,
  UnbindResult,
} from '@mytag/shared';
import { apiClient } from '@/lib/apiClient';

export interface DebugConfig {
  nodeEnv: string;
  mockMode: boolean;
  vernal: {
    baseUrl: string;
    clientCode: string;
    cid: string | null;
    timeoutMs: number;
    retries: number;
    envelopeMode: string;
    requestDataField: string;
    responseDataField: string;
    responseCodeField: string;
    successCodes: string[];
    decryptResponse: boolean;
  };
  crypto: {
    configured: boolean;
    mode: string;
    padding: string;
    keySource: string;
    keyFormat: string;
    keyStrategy: string;
    derivedKeyLength: number;
    keyPreview: string | null;
    selfTest: { ok: boolean; message: string };
  };
  sessionTtlHours: number;
  apiLogLimit: number;
}
export interface Geofence { id:string; name:string; latitude:number; longitude:number; radiusMeters:number; deviceNum:string; active:boolean; inside:boolean|null; createdAt:string; updatedAt:string }
export interface SavedReport { id:string; deviceNum:string; createdAt:string; from:string; to:string; notes:string; status:string; metrics:Record<string,unknown> }
export interface CampaignSummary {
  id: string;
  name: string;
  deviceNum: string;
  tester: string;
  startsAt: string;
  endsAt: string;
  location: string;
  notes: string;
  status: 'draft' | 'running' | 'completed';
  scenarios: Array<{ id: string; title: string; result: 'pending' | 'passed' | 'failed' | 'inconclusive' }>;
}
export interface CdcReportRequest { campaignId:string; deviceNums:string[]; from:string; to:string; tester:string; notes:string }

/**
 * Couche d'accès unique au backend MyTag.
 * Toute la logique API vit ici : les pages et hooks n'appellent jamais axios.
 */
export const mytagApi = {
  health: () => apiClient.get<HealthResult>('/health'),

  register: (payload: RegisterRequest) => apiClient.post<AuthResult>('/auth/register', payload),
  login: (payload: LoginRequest) => apiClient.post<AuthResult>('/auth/login', payload),
  session: () => apiClient.get<{ session: SessionInfo }>('/auth/session'),
  logout: () => apiClient.post<{ loggedOut: boolean }>('/auth/logout'),

  dashboard: () => apiClient.get<DashboardSummary>('/dashboard'),
  devices: (pageNo=1,pageSize=20) => apiClient.get<DeviceListResult>('/devices',{pageNo,pageSize}),
  device: (deviceNum: string) => apiClient.get<{ device: Device }>(`/devices/${deviceNum}`),
  deviceMetadata: (deviceNum: string) =>
    apiClient.get<{ metadata: LocalDeviceMetadata | null }>(`/devices/${deviceNum}/metadata`),
  updateDeviceMetadata: (deviceNum: string, update: LocalDeviceMetadataUpdate) =>
    apiClient.patch<{ metadata: LocalDeviceMetadata }>(`/devices/${deviceNum}/metadata`, update),
  archiveDevice: (deviceNum: string) =>
    apiClient.post<{ metadata: LocalDeviceMetadata }>(`/devices/${deviceNum}/archive`),
  restoreDevice: (deviceNum: string) =>
    apiClient.delete<{ metadata: LocalDeviceMetadata }>(`/devices/${deviceNum}/archive`),

  capabilities: () => apiClient.get<CapabilitiesResult>('/devices/capabilities'),
  bind: (deviceNum: string, name?: string, confirmReal = false) =>
    apiClient.post<BindResult>('/devices/bind', { deviceNum, ...(name ? { name } : {}), confirmReal }),
  unbind: (deviceNum: string, confirmReal = false) =>
    apiClient.post<UnbindResult>('/devices/unbind', { deviceNum, confirmReal }),

  refreshLocation: (deviceNum: string) =>
    apiClient.post<RefreshResult>(`/devices/${deviceNum}/refresh`),
  refreshLocations: (deviceNums: string[], confirmReal = false) =>
    apiClient.post<RefreshManyResult>('/devices/refresh', { deviceNums, confirmReal }),

  locations: (deviceNum: string, params: { from?: string; to?: string; limit?: number } = {}) =>
    apiClient.get<LocationHistoryResult>(`/devices/${deviceNum}/locations`, params),

  debugLogs: (limit = 100) =>
    apiClient.get<{ logs: ApiLogEntry[]; total: number }>('/debug/logs', { limit }),
  clearDebugLogs: () => apiClient.delete<{ cleared: boolean }>('/debug/logs'),
  debugConfig: () => apiClient.get<DebugConfig>('/debug/config'),
  resetMockData: () => apiClient.post<{ reset: boolean }>('/debug/mock/reset'),
  alerts: () => apiClient.get<{alerts:LocalAlert[];unread:number;positionStates:AlertPositionState[]}>('/alerts'),
  updateAlert: (id:string, status:Exclude<AlertStatus,'new'>) => apiClient.patch<{alert:LocalAlert|null}>(`/alerts/${id}`,{status}),
  geofences: () => apiClient.get<{geofences:Geofence[]}>('/geofences'),
  saveGeofence: (value:Omit<Geofence,'id'|'inside'|'createdAt'|'updatedAt'>, id?:string) => id ? apiClient.patch<{geofence:Geofence}>(`/geofences/${id}`,value) : apiClient.post<{geofence:Geofence}>('/geofences',value),
  deleteGeofence: (id:string) => apiClient.delete<{deleted:boolean}>(`/geofences/${id}`),
  reports: () => apiClient.get<{reports:SavedReport[]}>('/reports'),
  createReport: (value:{deviceNum:string;from:string;to:string;notes:string}) => apiClient.post<{report:SavedReport}>('/reports',value),
  downloadHistory: (format:'csv'|'pdf', params:{deviceNum:string;from:string;to:string}) => apiClient.download(`/exports/history.${format}`,params),
  downloadCdcReport: (value:CdcReportRequest) => apiClient.downloadPost('/reports/cdc.docx',value),
  campaigns: () => apiClient.get<{campaigns:CampaignSummary[]}>('/campaigns'),
  createCampaign: (value:Record<string,unknown>) => apiClient.post<{campaign:any}>('/campaigns',value),
  updateCampaign: (id:string,value:Record<string,unknown>) => apiClient.patch<{campaign:any}>(`/campaigns/${id}`,value),
  associations: () => apiClient.get<{associations:any[]}>('/associations'),
  saveAssociation: (value:Record<string,unknown>) => apiClient.post<{association:any}>('/associations',value),
  provisioning: () => apiClient.get<{items:any[]}>('/provisioning'),
  importProvisioning: (rows:Record<string,unknown>[]) => apiClient.post<{items:any[];valid:number;invalid:number}>('/provisioning/import',rows),
  measurements: (deviceNum:string,params:Record<string,unknown>) => apiClient.get<{measurement:any;retention:any}>(`/measurements/${deviceNum}`,params),
};
